import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import trainingsService from '../services/trainingsService';
import { buildFileUrl } from '../utils/fileUrl';
import { getEmbedUrl } from '../utils/embedUrl';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';
import {
  Plus,
  Edit,
  Trash2,
  Video,
  FileText as FileTextIcon,
  Link as LinkIcon,
  Download,
  Play,
  Eye,
  X,
  Save,
  GraduationCap,
  CheckCircle,
  Clock,
  Search,
  Filter,
  CheckCircle2,
  RotateCcw
} from 'lucide-react';

const Trainings = () => {
  const { hasRole } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [viewingTraining, setViewingTraining] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const queryClient = useQueryClient();

  const isManager = hasRole(['GERENCIA', 'ADMIN', 'CEO']);

  // Estados do formulário
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'VIDEO',
    video_url: '',
    external_link: '',
    duration_minutes: '',
    allowed_roles: []
  });

  // Query para buscar treinamentos
  const { data: trainingsData, isLoading } = useQuery({
    queryKey: ['trainings'],
    queryFn: () => trainingsService.getTrainings()
  });

  const trainings = trainingsData?.data || [];

  // Filtragem e busca
  const filteredTrainings = useMemo(() => {
    return trainings.filter(t => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchTitle = t.title?.toLowerCase().includes(term);
        const matchDesc = t.description?.toLowerCase().includes(term);
        if (!matchTitle && !matchDesc) return false;
      }
      if (typeFilter !== 'ALL' && t.type !== typeFilter) {
        return false;
      }
      if (statusFilter === 'COMPLETED' && !t.user_completed) {
        return false;
      }
      if (statusFilter === 'PENDING' && t.user_completed) {
        return false;
      }
      return true;
    });
  }, [trainings, searchTerm, typeFilter, statusFilter]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => trainingsService.createTraining(data),
    onSuccess: () => {
      toast.success('Treinamento criado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['trainings'] });
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar treinamento');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => trainingsService.updateTraining(id, data),
    onSuccess: () => {
      toast.success('Treinamento atualizado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['trainings'] });
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar treinamento');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => trainingsService.deleteTraining(id),
    onSuccess: () => {
      toast.success('Treinamento deletado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['trainings'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao deletar treinamento');
    }
  });

  const viewMutation = useMutation({
    mutationFn: ({ id, data }) => trainingsService.registerView(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainings'] });
    }
  });

  const openModal = (training = null) => {
    if (training) {
      setEditingTraining(training);
      setFormData({
        title: training.title || '',
        description: training.description || '',
        type: training.type || 'VIDEO',
        video_url: training.video_url || '',
        external_link: training.external_link || '',
        duration_minutes: training.duration_minutes || '',
        allowed_roles: training.allowed_roles || []
      });
    } else {
      setEditingTraining(null);
      setFormData({
        title: '',
        description: '',
        type: 'VIDEO',
        video_url: '',
        external_link: '',
        duration_minutes: '',
        allowed_roles: []
      });
    }
    setSelectedFile(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTraining(null);
    setFormData({
      title: '',
      description: '',
      type: 'VIDEO',
      video_url: '',
      external_link: '',
      duration_minutes: '',
      allowed_roles: []
    });
    setSelectedFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (createMutation.isPending || updateMutation.isPending) return;

    if (!formData.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }

    const submitData = new FormData();
    submitData.append('title', formData.title.trim());
    submitData.append('description', formData.description || '');
    submitData.append('type', formData.type);
    
    if (formData.type === 'VIDEO') {
      if (selectedFile) {
        submitData.append('file', selectedFile);
      } else if (formData.video_url) {
        submitData.append('video_url', formData.video_url);
      }
    } else if (formData.type === 'DOCUMENT') {
      if (!selectedFile && !editingTraining) {
        toast.error('É necessário enviar um arquivo');
        return;
      }
      if (selectedFile) {
        submitData.append('file', selectedFile);
      }
    } else if (formData.type === 'LINK') {
      if (!formData.external_link) {
        toast.error('URL externa é obrigatória');
        return;
      }
      submitData.append('external_link', formData.external_link);
    }

    if (formData.duration_minutes) {
      submitData.append('duration_minutes', formData.duration_minutes);
    }

    // Enviar allowed_roles sempre (mesmo se vazio, para indicar "todos")
    submitData.append('allowed_roles', JSON.stringify(formData.allowed_roles));

    if (editingTraining) {
      updateMutation.mutate({ id: editingTraining.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleDelete = (training) => {
    setConfirmModal({
      show: true,
      message: `Tem certeza que deseja deletar o treinamento "${training.title}"?`,
      onConfirm: () => {
        deleteMutation.mutate(training.id);
        setConfirmModal({ show: false, message: '', onConfirm: null });
      }
    });
  };

  const handleView = async (training) => {
    setViewingTraining(training);
    // Registrar visualização sem sobrescrever conclusão
    viewMutation.mutate({ id: training.id, data: { progress_percentage: 0 } });
  };

  const handleToggleCompleted = (training) => {
    if (!training) return;
    viewMutation.mutate(
      { id: training.id, data: { toggle_completed: true } },
      {
        onSuccess: () => {
          setViewingTraining(prev => prev ? ({ ...prev, user_completed: !prev.user_completed }) : null);
          toast.success(!training.user_completed ? 'Treinamento concluído com sucesso!' : 'Status do treinamento revertido para pendente');
        }
      }
    );
  };

  const handleRoleToggle = (role) => {
    setFormData(prev => {
      const roles = prev.allowed_roles || [];
      if (roles.includes(role)) {
        return { ...prev, allowed_roles: roles.filter(r => r !== role) };
      } else {
        return { ...prev, allowed_roles: [...roles, role] };
      }
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'VIDEO':
        return <Video className="w-5 h-5" />;
      case 'DOCUMENT':
        return <FileTextIcon className="w-5 h-5" />;
      case 'LINK':
        return <LinkIcon className="w-5 h-5" />;
      default:
        return <FileTextIcon className="w-5 h-5" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'VIDEO':
        return 'Vídeo';
      case 'DOCUMENT':
        return 'Documento';
      case 'LINK':
        return 'Link Externo';
      default:
        return type;
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      'GERENCIA': 'Gerência',
      'RECEPCAO_COMPRAS_AGENDA': 'Recepção - Compras & Agenda',
      'RECEPCAO_PROJETOS': 'Recepção - Projetos',
      'PRODUCAO': 'Produção',
      'MONTAGEM_CLIENTE': 'Montagem - Cliente'
    };
    return labels[role] || role;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Carregando treinamentos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Treinamentos</h1>
          <p className="text-gray-600">Acesse materiais de treinamento e capacitação</p>
        </div>
        {isManager && (
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Novo Treinamento
          </button>
        )}
      </div>

      {/* Barra de Filtros e Busca */}
      <div className="bg-white rounded-xl shadow-xs border border-gray-200 p-4 mb-6 space-y-3">
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por título ou descrição do treinamento..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent outline-none"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 bg-gray-50 p-1 rounded-lg border border-gray-200 text-xs">
              <span className="text-gray-500 font-medium px-1 flex items-center gap-1">
                <Filter className="w-3.5 h-3.5" /> Tipo:
              </span>
              {[
                { value: 'ALL', label: 'Todos' },
                { value: 'VIDEO', label: 'Vídeos' },
                { value: 'DOCUMENT', label: 'Documentos' },
                { value: 'LINK', label: 'Links' }
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setTypeFilter(opt.value)}
                  className={`px-2 py-1 rounded-md transition-colors font-medium ${
                    typeFilter === opt.value
                      ? 'bg-white text-gray-900 shadow-2xs'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1.5 bg-gray-50 p-1 rounded-lg border border-gray-200 text-xs">
              <span className="text-gray-500 font-medium px-1">Status:</span>
              {[
                { value: 'ALL', label: 'Todos' },
                { value: 'COMPLETED', label: 'Concluídos' },
                { value: 'PENDING', label: 'Pendentes' }
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setStatusFilter(opt.value)}
                  className={`px-2 py-1 rounded-md transition-colors font-medium ${
                    statusFilter === opt.value
                      ? 'bg-white text-gray-900 shadow-2xs'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-gray-500 pt-1">
          <span>Mostrando <strong>{filteredTrainings.length}</strong> de <strong>{trainings.length}</strong> treinamentos</span>
          {(searchTerm || typeFilter !== 'ALL' || statusFilter !== 'ALL') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setTypeFilter('ALL');
                setStatusFilter('ALL');
              }}
              className="text-[#49943d] hover:underline font-medium"
            >
              Limpar filtros
            </button>
          )}
        </div>
      </div>

      {/* Lista de Treinamentos */}
      {filteredTrainings.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <GraduationCap className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            {trainings.length === 0 ? 'Nenhum treinamento disponível' : 'Nenhum treinamento encontrado com os filtros aplicados'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTrainings.map((training) => (
            <div
              key={training.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow flex flex-col justify-between h-full"
            >
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg text-blue-600 shrink-0">
                      {getTypeIcon(training.type)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 line-clamp-1">{training.title}</h3>
                      <p className="text-sm text-gray-500">{getTypeLabel(training.type)}</p>
                    </div>
                  </div>
                  {isManager && (
                    <div className="flex gap-1 shrink-0">
                      <button
                        onClick={() => openModal(training)}
                        className="p-1 text-blue-600 hover:text-blue-800"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(training)}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Deletar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {training.description ? (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {training.description}
                  </p>
                ) : (
                  <div className="mb-4"></div>
                )}

                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4 mt-auto">
                  {training.duration_minutes && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatDuration(training.duration_minutes)}
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {training.total_views === 1 ? '1 visualização' : `${training.total_views || 0} visualizações`}
                  </div>
                </div>

                <div className="mb-2">
                  <p className="text-xs text-gray-500 mb-1">Disponível para:</p>
                  <div className="flex flex-wrap gap-1">
                    {training.allowed_roles && training.allowed_roles.length > 0 ? (
                      training.allowed_roles.map((role) => (
                        <span
                          key={role}
                          className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded"
                        >
                          {getRoleLabel(role)}
                        </span>
                      ))
                    ) : (
                      <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded border border-emerald-200">
                        Todos os setores
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-6 pt-0 border-t border-gray-100 mt-2">
                <div className="flex gap-2 pt-4">
                  <button
                    onClick={() => handleView(training)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold"
                  >
                    <Play className="w-4 h-4" />
                    Assistir
                  </button>
                  {training.type === 'DOCUMENT' && training.file_path && (
                    <button
                      onClick={() => {
                        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                        window.open(`${baseUrl}${training.file_path}`, '_blank');
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {training.user_completed && (
                  <div className="mt-3 flex items-center gap-2 text-green-600 text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    Concluído
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Criação/Edição (apenas GERENCIA) */}
      {isModalOpen && isManager && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingTraining ? 'Editar Treinamento' : 'Novo Treinamento'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                  placeholder="Título do treinamento"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                  rows={3}
                  placeholder="Descrição do treinamento"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => {
                    setFormData({ ...formData, type: e.target.value });
                    setSelectedFile(null);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                  required
                >
                  <option value="VIDEO">Vídeo</option>
                  <option value="DOCUMENT">Documento</option>
                  <option value="LINK">Link Externo</option>
                </select>
              </div>

              {formData.type === 'VIDEO' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Arquivo de Vídeo
                    </label>
                    <input
                      type="file"
                      accept=".mp4,.webm,.ogg,.mov,.avi,.wmv"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    />
                    {editingTraining?.file_name && !selectedFile && (
                      <p className="text-sm text-gray-500 mt-1">
                        Arquivo atual: {editingTraining.file_name}
                      </p>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">ou</div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL do Vídeo (YouTube, Vimeo, etc.)
                    </label>
                    <input
                      type="url"
                      value={formData.video_url}
                      onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                </>
              )}

              {formData.type === 'DOCUMENT' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Arquivo *
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    required={!editingTraining}
                  />
                  {editingTraining?.file_name && !selectedFile && (
                    <p className="text-sm text-gray-500 mt-1">
                      Arquivo atual: {editingTraining.file_name}
                    </p>
                  )}
                </div>
              )}

              {formData.type === 'LINK' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL Externa *
                  </label>
                  <input
                    type="url"
                    value={formData.external_link}
                    onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="https://..."
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duração (minutos)
                </label>
                <input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                  placeholder="Ex: 30"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Disponível para (deixe vazio para todos)
                </label>
                <div className="space-y-2">
                  {['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS', 'PRODUCAO', 'MONTAGEM_CLIENTE'].map((role) => (
                    <label key={role} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.allowed_roles.includes(role)}
                        onChange={() => handleRoleToggle(role)}
                        className="rounded border-gray-300 text-[#49943d] focus:ring-[#49943d]"
                      />
                      <span className="ml-2 text-sm text-gray-700">{getRoleLabel(role)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {createMutation.isPending || updateMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editingTraining ? 'Atualizar' : 'Criar'} Treinamento
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Visualização */}
      {viewingTraining && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">{viewingTraining.title}</h2>
              <button
                onClick={() => setViewingTraining(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {viewingTraining.description && (
                <p className="text-gray-600 mb-6">{viewingTraining.description}</p>
              )}

              {viewingTraining.type === 'VIDEO' && (
                <div className="mb-6">
                  {viewingTraining.file_path ? (
                    <video
                      controls
                      className="w-full rounded-lg"
                      onTimeUpdate={(e) => {
                        const video = e.target;
                        const progress = (video.currentTime / video.duration) * 100;
                        if (progress > 0 && progress % 10 < 1) {
                          viewMutation.mutate({
                            id: viewingTraining.id,
                            data: {
                              progress_percentage: Math.round(progress),
                              completed: progress >= 90
                            }
                          });
                        }
                      }}
                      onEnded={() => {
                        viewMutation.mutate({
                          id: viewingTraining.id,
                          data: { progress_percentage: 100, completed: true }
                        });
                        toast.success('Treinamento concluído!');
                      }}
                    >
                      <source
                        src={buildFileUrl(viewingTraining.file_path)}
                        type={viewingTraining.file_type || 'video/mp4'}
                      />
                      Seu navegador não suporta o elemento de vídeo.
                    </video>
                  ) : viewingTraining.video_url ? (
                    <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                      <iframe
                        src={getEmbedUrl(viewingTraining.video_url)}
                        title={viewingTraining.title || 'Vídeo de Treinamento'}
                        className="w-full h-full rounded-lg"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  ) : (
                    <p className="text-gray-500">Vídeo não disponível</p>
                  )}
                </div>
              )}

              {viewingTraining.type === 'DOCUMENT' && viewingTraining.file_path && (
                <div className="mb-6">
                    <iframe
                    src={buildFileUrl(viewingTraining.file_path)}
                    className="w-full h-[600px] rounded-lg border"
                    title={viewingTraining.title}
                  ></iframe>
                  <div className="mt-4">
                    <a
                      href={buildFileUrl(viewingTraining.file_path)}
                      download
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Download className="w-4 h-4" />
                      Baixar Documento
                    </a>
                  </div>
                </div>
              )}

              {viewingTraining.type === 'LINK' && viewingTraining.external_link && (
                <div className="mb-6">
                  <a
                    href={viewingTraining.external_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <LinkIcon className="w-4 h-4" />
                    Abrir Link Externo
                  </a>
                </div>
              )}

              <div className="flex items-center justify-between gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => handleToggleCompleted(viewingTraining)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    viewingTraining.user_completed
                      ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border border-emerald-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  <CheckCircle className="w-4 h-4" />
                  {viewingTraining.user_completed ? 'Concluído ✓ (Clique para desmarcar)' : 'Marcar como Concluído'}
                </button>

                <button
                  onClick={() => setViewingTraining(null)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-semibold"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmModal.show}
        isOpen={confirmModal.show}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
        type="danger"
        title="Confirmar Exclusão"
        confirmText="Deletar"
        cancelText="Cancelar"
      />
    </div>
  );
};

export default Trainings;

